import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Building2, LogOut, Loader2, CheckCircle } from "lucide-react";

interface StaffProfile {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  businesses: { id: number; name: string; role: string }[];
  activeBusinessId: number | null;
}

export default function StaffDashboard() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [profile, setProfile] = useState<StaffProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch("/api/staff/auth/me", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          navigate("/staff-portal/login");
          return;
        }
        setProfile(await res.json());
      })
      .catch(() => navigate("/staff-portal/login"))
      .finally(() => setIsLoading(false));
  }, [navigate]);

  const handleSelectBusiness = async (businessId: number) => {
    try {
      const res = await fetch("/api/staff/my/select-business", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ businessId }),
      });
      if (res.ok) {
        try {
          if (profile?.id) localStorage.setItem(`activeBusinessId_user_${profile.id}`, String(businessId));
          localStorage.setItem('activeBusinessId', String(businessId));
        } catch {}
        window.location.href = "/dashboard";
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Error", description: err.message || "Failed to select business", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleLogout = async () => {
    await fetch("/api/staff/auth/logout", { method: "POST", credentials: "include" });
    navigate("/staff-portal/login");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/ZentroBooksLogo.png" alt="ZentroBooks" className="h-8" />
          <span className="text-sm text-muted-foreground">Staff Portal</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">{profile.full_name || profile.email}</span>
          <Badge variant="secondary" className="capitalize">{profile.role}</Badge>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Welcome, {profile.first_name || "Staff"}</h1>
          <p className="text-muted-foreground">Select a business to get started</p>
        </div>

        {profile.businesses.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No businesses assigned</h3>
              <p className="text-muted-foreground">Contact your administrator to get access to businesses</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {profile.businesses.map((biz) => (
              <Card
                key={biz.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  profile.activeBusinessId === biz.id ? "ring-2 ring-primary" : ""
                }`}
                onClick={() => handleSelectBusiness(biz.id)}
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{biz.name}</h3>
                    <p className="text-sm text-muted-foreground capitalize">Role: {biz.role}</p>
                  </div>
                  {profile.activeBusinessId === biz.id && (
                    <CheckCircle className="w-5 h-5 text-primary" />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
